import { InfoOutlined } from "@mui/icons-material";
import { Button, Chip, IconButton, MenuItem, TextField, Box, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import HighlightedText from "components/Shared/HighlightedText";
import { useAppMeta, useAuth, useTablePagination } from "hooks";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLazyGetLeadListQuery, useLazyGetWonStatsQuery } from "store/services/vendorLeadApi";
import { localizeDateFull } from "utils/dateFormats";
import CustomTabs from "components/Shared/CustomTabs";
import TableFilter from "components/Shared/AppTable/TableFilter";
import AppTable from "components/Shared/AppTable";
import { useCustomParams } from "hooks/useCustomParams";
import UploadVendorLeadDialog from "./vendorLeadComponents/UploadVendorLeadDialog";

const STATUS_TABS = [
    { label: "All", value: "All" },
    { label: "New", value: "New" },
    { label: "Contacted", value: "Contacted" },
    { label: "Quote Sent", value: "QuoteSent" },
    { label: "Negotiation", value: "Negotiation" },
    { label: "Won", value: "Won" },
    { label: "Lost", value: "Lost" },
    { label: "Inactive", value: "Inactive" },
    { label: "Assigned to Me", value: "AssignedToMe" },
];

const ASSIGNED_OPTIONS = [
    { label: "All", value: "" },
    { label: "Assigned", value: "true" },
    { label: "Unassigned", value: "false" },
];

const SELF_ONLY_TABS = new Set(["Contacted", "QuoteSent", "Negotiation", "Won", "Lost"]);

const breadcrumbs = [{ title: "Leads" }];

export const VendorLeadListScreen = () => {
    const { params } = useCustomParams();
    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();

    const keyword = params?.search || "";
    const activeTab = params?.tab || "All";

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [assigned, setAssigned] = useState("");
    const [assignedStatus, setAssignedStatus] = useState("");

    const { loggedInUser, isAccountAdmin, isSystemAdmin, isEntityAdmin } = useAuth();
    const loggedInUserId = loggedInUser?._id;
    const isEntityUser = !isAccountAdmin && !isSystemAdmin && !isEntityAdmin;


    const isAssignedToMe = activeTab === "AssignedToMe";
    const isInactive = activeTab === "Inactive";
    const isAll = activeTab === "All";
    const isSelfOnly = SELF_ONLY_TABS.has(activeTab) && isEntityUser;

    const [fetchLeads, { data: leadRes, isLoading, isFetching }] =
        useLazyGetLeadListQuery();

    const [fetchWonStats, { data: wonStats }] = useLazyGetWonStatsQuery();

    const FOLLOWUP_TABS = new Set(["Contacted", "QuoteSent", "Negotiation"]);
    const showFollowupCols = FOLLOWUP_TABS.has(activeTab);

    const statusColumn = (isAll || isAssignedToMe)
        ? [
            {
                name: "Status",
                selector: (row) => row?.status,
                cell: (row) => <div>{row?.status ?? "N/A"}</div>,
                minWidth: "120px",
            },
        ]
        : [];

    const followupColumns = showFollowupCols
        ? [
            {
                name: "Last Changed",
                selector: (row) => row?.lastChanged,
                cell: (row) => <div>{row?.lastChanged ?? "N/A"}</div>,
                minWidth: "120px",
            },
            {
                name: "Upcoming Followup",
                selector: (row) => row?.upcomingFollowup,
                cell: (row) => <div>{row?.upcomingFollowup ?? "N/A"}</div>,
                minWidth: "150px",
            },
        ]
        : [];

    const columns = [
        {
            name: "Lead ID",
            selector: (row) => row?.leadId,
            cell: (row) => <div>{row?.leadId ?? "N/A"}</div>,
            minWidth: "130px",
        },
        {
            name: "Name",
            selector: (row) => row?.name,
            cell: (row) => <div>{row?.name ?? "N/A"}</div>,
            minWidth: "150px",
        },
        {
            name: "Company",
            selector: (row) => row?.companyName,
            cell: (row) => (
                <div title={row?.companyName} className="line-clamp-1">
                    {row?.companyName ? (
                        <HighlightedText text={row.companyName} highlight={keyword} />
                    ) : "N/A"}
                </div>
            ),
            minWidth: "150px",
        },
        {
            name: "Phone",
            selector: (row) => row?.phone,
            cell: (row) => <div>{row?.phone ?? "N/A"}</div>,
            minWidth: "150px",
        },
        {
            name: "Alternate Phone",
            selector: (row) => row?.alternatePhone,
            cell: (row) => <div>{row?.alternatePhone ?? "N/A"}</div>,
            minWidth: "150px",
        },
        {
            name: "Responsible",
            selector: (row) => row?.responsible?.name,
            cell: (row) => (
                <div title={row?.responsible?.name} className="line-clamp-1">
                    {row?.responsible?.name || "Unassigned"}
                </div>
            ),
            minWidth: "150px",
        },
        {
            name: "Score",
            selector: (row) => row?.score,
            cell: (row) => (
                <div className="font-medium">
                    {row?.score != null ? `${row.score}/100` : "N/A"}
                </div>
            ),
            minWidth: "90px",
        },
        ...statusColumn,
        {
            name: "Created At",
            selector: (row) => row?.createdAt,
            cell: (row) => (
                <div title={localizeDateFull(row.createdAt)} className="line-clamp-1">
                    {row?.createdAt ? localizeDateFull(row.createdAt) : "N/A"}
                </div>
            ),
            minWidth: "160px",
        },
        ...followupColumns,
        {
            name: "Actions",
            cell: (row) => {
                const isEditDisabled = ["Won", "Lost"].includes(row.status);
                return (
                    <>
                        <Link to={`/app/crm/vendor-lead/${row._id}/detail`}>
                            <IconButton size="small" aria-label="View Lead">
                                <InfoOutlined color="primary" />
                            </IconButton>
                        </Link>
                        <Link
                            to={`/app/crm/vendor-lead/${row._id}/edit`}
                            style={{ pointerEvents: isEditDisabled ? "none" : "auto" }}
                        >
                            <IconButton
                                size="small"
                                aria-label="Edit Lead"
                                disabled={isEditDisabled}
                            >
                                <ModeEditOutlineTwoToneIcon />
                            </IconButton>
                        </Link>
                    </>
                );
            },
            ignoreRowClick: true,
            right: true,
            minWidth: "100px",
        },
    ];

    React.useEffect(() => {
        fetchLeads({
            isActive: isInactive ? false : true,
            ...(!isInactive && !isAssignedToMe && !isAll && { status: activeTab }),
            ...((isAssignedToMe || isSelfOnly) && { responsible: loggedInUserId }),
            ...(isAssignedToMe && assignedStatus && { status: assignedStatus }),
            keyword,
            page: currentPage,
            limit: perPage,
            ...(startDate && { startDate }),
            ...(endDate && { endDate }),
            ...(assigned && { assigned }),
        });
    }, [activeTab, currentPage, fetchLeads, keyword, perPage, startDate, endDate, assigned, isInactive, isAssignedToMe, isSelfOnly, loggedInUserId, assignedStatus, isAll]);

    React.useEffect(() => {
        if (activeTab !== "Won") return;
        fetchWonStats({
            ...(startDate && { startDate }),
            ...(endDate && { endDate }),
        });
    }, [activeTab, startDate, endDate, fetchWonStats]);

    useAppMeta(isAll ? "All Leads" : `${activeTab} Leads`);

    return (
        <BreadcrumbLayout
            breadcrumbs={breadcrumbs}
            isBusy={isFetching || isLoading}
            headerActions={<PageActions />}
        >
            <BreadcrumbLayout.Paper>
                <CustomTabs tabs={STATUS_TABS} activeTab={activeTab} />

                {/* ── Filters ── */}
                <Box sx={{ px: 2, pt: 2, pb: 1, display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {activeTab === "Won" && (
                        <Box
                            sx={{
                                mr: "auto",
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                px: 2.5,
                                py: 1.2,
                                background: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
                                borderLeft: "3px solid #16a34a",
                                boxShadow: "0 1px 4px rgba(22,163,74,0.10)",
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="caption"
                                    sx={{ color: "#15803d", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", display: "block", lineHeight: 1 }}
                                >
                                    Avg Won Time
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mt: 0.4 }}>
                                    <Typography variant="body1" fontWeight={700} sx={{ color: "#14532d", lineHeight: 1 }}>
                                        {wonStats?.avgWonTime ?? "—"}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: "#16a34a" }}>
                                        {wonStats?.totalCount ?? 0} leads
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    )}

                    <TextField
                        select
                        label="Assigned"
                        value={assigned}
                        onChange={(e) => setAssigned(e.target.value)}
                        size="small"
                        sx={{ minWidth: 140 }}
                    >
                        {ASSIGNED_OPTIONS.map((o) => (
                            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                    />

                    <TextField
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                    />

                    {isAssignedToMe && (
                        <TextField
                            select
                            label="Status"
                            value={assignedStatus}
                            onChange={(e) => setAssignedStatus(e.target.value)}
                            size="small"
                            sx={{ minWidth: 150 }}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="New">New</MenuItem>
                            <MenuItem value="Contacted">Contacted</MenuItem>
                            <MenuItem value="QuoteSent">Quote Sent</MenuItem>
                            <MenuItem value="Negotiation">Negotiation</MenuItem>
                            <MenuItem value="Won">Won</MenuItem>
                            <MenuItem value="Lost">Lost</MenuItem>
                        </TextField>
                    )}

                    <TableFilter searchLabel="Name / Company / Email / Phone / Lead ID">
                    </TableFilter>

                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => { setStartDate(""); setEndDate(""); setAssigned(""); setAssignedStatus(""); }}
                        disabled={!startDate && !endDate && !assigned && !assignedStatus}
                    >
                        Clear
                    </Button>

                </Box>

                <AppTable
                    columns={columns}
                    data={leadRes?.data}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                    progressPending={isLoading || isFetching}
                    paginationTotalRows={leadRes?.totalSize ?? 0}
                />
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};

const PageActions = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <UploadVendorLeadDialog />
        <Link to="/app/crm/vendor-lead/assign">
            <Button variant="outlined">Assign Responsible</Button>
        </Link>
        <Link to="/app/crm/vendor-lead/create">
            <Button startIcon={<AddIcon />} variant="contained">
                New Vendor Lead
            </Button>
        </Link>
    </div>
);